// v3/app/router/FeatureRoute.tsx — Feature permission guard
// Accepts featureId prop.
// Uses usePermission().canAccessFeature(featureId).
// Shows access denied message if denied.
// Renders Outlet if allowed.
// Zero imports from v1/v2.

import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import { usePermission } from '../../shared/hooks/usePermission';
import { Button } from '../../design-system/primitives';

interface FeatureRouteProps {
  featureId: string;
}

export function FeatureRoute({ featureId }: FeatureRouteProps) {
  const { canAccessFeature } = usePermission();
  const navigate = useNavigate();

  if (!canAccessFeature(featureId)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center space-y-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--ch-danger-bg)]">
          <ShieldX className="h-8 w-8 text-[var(--ch-danger-fg)]" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Access Denied</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          You don't have permission to access this page. Please contact your administrator if you believe this is an error.
        </p>
        <Button variant="outline" onClick={() => navigate('/', { replace: true })}>
          Go to Dashboard
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
